import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import channelConnectionsService from '../services/channelConnectionsService';

const WhatsAppConnectionContext = createContext({
  connections: [],
  activeConnection: null,
  activeConnectionId: null,
  loading: true,
  error: null,
  setActiveConnectionId: () => {},
  refreshConnections: async () => {},
});

export const useWhatsAppConnection = () => useContext(WhatsAppConnectionContext);
export const useChannelConnections = useWhatsAppConnection;

export const WhatsAppConnectionProvider = ({ children }) => {
  const [connections, setConnections] = useState([]);
  const [activeConnectionId, setActiveConnectionIdState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar conexiones al montar
  const loadConnections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await channelConnectionsService.getAll();
      const activeConnections = (data || []).filter(c => c.is_active);
      setConnections(activeConnections);
      
      // Si hay conexiones y no hay una seleccionada, seleccionar la primera
      // También verificar localStorage por si había una guardada
      const savedConnectionId = localStorage.getItem('activeChannelConnectionId')
        || localStorage.getItem('activeWhatsAppConnectionId');
      
      if (activeConnections.length > 0) {
        // Si hay una guardada y existe en la lista, usarla
        if (savedConnectionId && activeConnections.find(c => c.id === savedConnectionId)) {
          setActiveConnectionIdState(savedConnectionId);
        } else {
          // Si no, usar la primera conexión
          setActiveConnectionIdState(activeConnections[0].id);
          localStorage.setItem('activeChannelConnectionId', activeConnections[0].id);
        }
      }
      
      console.log('💬 [MULTICANAL] Conexiones cargadas:', activeConnections.length);
    } catch (err) {
      console.error('Error loading WhatsApp connections:', err);
      setError('Error cargando canales de mensajería');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkAuthAndLoad = () => {
      const token = localStorage.getItem('token');
      if (token) {
        loadConnections();
      } else {
        setLoading(false);
      }
    };

    // Verificación inicial
    checkAuthAndLoad();

    // Escuchar evento custom para cuando se loguea
    window.addEventListener('auth-success', checkAuthAndLoad);
    return () => {
      window.removeEventListener('auth-success', checkAuthAndLoad);
    };
  }, [loadConnections]);

  // Función para cambiar la conexión activa
  const setActiveConnectionId = useCallback((connectionId) => {
    setActiveConnectionIdState(connectionId);
    if (connectionId) {
      localStorage.setItem('activeWhatsAppConnectionId', connectionId);
      localStorage.setItem('activeChannelConnectionId', connectionId);
      const selected = connections.find((connection) => connection.id === connectionId);
      if (selected) localStorage.setItem('activeChannelType', selected.channel || 'whatsapp');
      console.log('💬 [MULTICANAL] Conexión activa cambiada a:', connectionId);
    } else {
      localStorage.removeItem('activeChannelConnectionId');
      localStorage.removeItem('activeWhatsAppConnectionId');
      localStorage.removeItem('activeChannelType');
    }
  }, [connections]);

  // Obtener la conexión activa completa
  const activeConnection = connections.find(c => c.id === activeConnectionId) || null;
  const whatsappConnections = connections.filter(c => (c.channel || 'whatsapp') === 'whatsapp');
  const socialConnections = connections.filter(c => c.channel === 'facebook' || c.channel === 'instagram');
  const activeWhatsAppConnection = whatsappConnections.find(c => c.id === activeConnectionId)
    || whatsappConnections[0]
    || null;

  // Refrescar conexiones (útil después de agregar/eliminar)
  const refreshConnections = useCallback(async () => {
    await loadConnections();
  }, [loadConnections]);

  return (
    <WhatsAppConnectionContext.Provider value={{
      connections,
      whatsappConnections,
      socialConnections,
      activeConnection,
      activeWhatsAppConnection,
      activeWhatsAppConnectionId: activeWhatsAppConnection?.id || null,
      activeConnectionId,
      loading,
      error,
      setActiveConnectionId,
      refreshConnections,
    }}>
      {children}
    </WhatsAppConnectionContext.Provider>
  );
};

export default WhatsAppConnectionContext;
